import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { Plus, Calendar, Trash2, Edit } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import RightSlideModal from '../../components/RightSlideModal';

export default function HomeworkPage() {
  const { user } = useAuth();
  const [homework, setHomework] = useState([]);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedHw, setSelectedHw] = useState(null);
  const [formData, setFormData] = useState({ title: '', description: '', classId: '', subjectId: '', dueDate: '' });
  const [selectedSession, setSelectedSession] = useState('');
  const [modalSession, setModalSession] = useState('');
  const [sessions, setSessions] = useState([]);
  const [selectedClassFilter, setSelectedClassFilter] = useState('all');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [homeworkRes, classesRes, subjectsRes, sessionsRes] = await Promise.all([
        api.get('/homework'),
        api.get('/classes'),
        api.get('/subjects'),
        api.get('/academic-sessions')
      ]);
      setHomework(homeworkRes.data);
      setClasses(classesRes.data);
      setSessions(sessionsRes.data);
      setSubjects(subjectsRes.data);
      if (sessionsRes.data.length > 0 && !selectedSession) {
        setSelectedSession(sessionsRes.data[sessionsRes.data.length - 1].sessionYear);
      }
    } catch (error) {
      toast.error('Failed to fetch homework');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedHw) {
        await api.put(`/homework/${selectedHw.id}`, formData);
        toast.success('Homework updated successfully');
      } else {
        await api.post('/homework', formData);
        toast.success('Homework assigned successfully');
      }
      setShowModal(false);
      setFormData({ title: '', description: '', classId: '', subjectId: '', dueDate: '' });
      setSelectedHw(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to save homework');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this homework?')) return;
    try {
      await api.delete(`/homework/${id}`);
      toast.success('Homework deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete homework');
    }
  };

  const isTeacherOrAdmin = user?.role === 'admin' || user?.role === 'teacher';

  const getSubjectsForClass = (classId) => {
    return subjects.filter(s => s.classId === classId);
  };

  const filteredHomework = homework.filter(hw => {
    const hwClass = classes.find(c => c.id === hw.classId);
    const sessionMatch = !selectedSession || selectedSession === 'all' || hwClass?.sessionYear === selectedSession;
    const classMatch = selectedClassFilter === 'all' || hw.classId === selectedClassFilter;
    return sessionMatch && classMatch;
  });

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="font-outfit font-semibold text-2xl text-slate-800">Homework</h2>
          <div className="flex items-center gap-3 mt-1">
            <select 
              value={selectedSession} 
              onChange={(e) => {
                setSelectedSession(e.target.value);
                setSelectedClassFilter('all');
              }}
              className="bg-indigo-50 text-indigo-700 text-xs font-bold px-2 py-1 rounded-lg border-none focus:ring-2 focus:ring-indigo-100 outline-none cursor-pointer"
            >
              <option value="all">All Sessions</option>
              {sessions.map(s => <option key={s.id} value={s.sessionYear}>{s.sessionYear}</option>)}
            </select>
            <div className="h-4 w-px bg-slate-200" />
            <div className="flex gap-1 overflow-x-auto pb-1 no-scrollbar max-w-[500px]">
              <button 
                onClick={() => setSelectedClassFilter('all')}
                className={`px-3 py-1 rounded-lg text-xs font-medium transition-all ${selectedClassFilter === 'all' ? 'bg-indigo-600 text-white shadow-sm' : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-100'}`}
              >
                All Classes
              </button>
              {classes.filter(c => !selectedSession || selectedSession === 'all' || c.sessionYear === selectedSession).map(c => (
                <button 
                  key={c.id}
                  onClick={() => setSelectedClassFilter(c.id)}
                  className={`px-3 py-1 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${selectedClassFilter === c.id ? 'bg-indigo-600 text-white shadow-sm' : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-100'}`}
                >
                  {c.className}
                </button>
              ))}
            </div>
          </div>
        </div>
        {isTeacherOrAdmin && (
          <button onClick={() => { 
            setSelectedHw(null); 
            setFormData({ title: '', description: '', classId: '', subjectId: '', dueDate: '' }); 
            setModalSession(selectedSession === 'all' ? (sessions[0]?.sessionYear || '') : selectedSession);
            setShowModal(true); 
          }} className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full px-6 py-2.5 font-medium shadow-sm transition-all flex items-center gap-2">
            <Plus className="w-5 h-5" />
            Assign Homework
          </button>
        )}
      </div>

      {filteredHomework.length > 0 ? (
        <div className="space-y-4">
          {filteredHomework.map(hw => {
            const hwClass = classes.find(c => c.id === hw.classId);
            const subject = subjects.find(s => s.id === hw.subjectId);
            const isOverdue = new Date(hw.dueDate) < new Date();
            
            return (
              <div key={hw.id} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 hover:shadow-md transition-shadow group">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] uppercase font-bold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full">{hwClass?.sessionYear || 'No Session'}</span>
                      <h3 className="font-outfit font-semibold text-lg text-slate-800">{hw.title}</h3>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-slate-500">
                      <span className="font-medium text-slate-700">{hwClass?.className}</span>
                      <span>•</span>
                      <span>{subject?.subjectName}</span>
                      <span>•</span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4 text-indigo-400" />
                        Due: {new Date(hw.dueDate).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${isOverdue ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                      {isOverdue ? 'Overdue' : 'Active'}
                    </span>
                    {isTeacherOrAdmin && (
                      <>
                        <button onClick={() => { setSelectedHw(hw); setFormData({ title: hw.title, description: hw.description, classId: hw.classId, subjectId: hw.subjectId, dueDate: hw.dueDate }); setShowModal(true); }} className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">
                          <Edit className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleDelete(hw.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
                <p className="text-slate-600">{hw.description}</p>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-12 text-center">
          <Calendar className="w-16 h-16 text-indigo-200 mx-auto mb-4" />
          <h3 className="font-outfit font-semibold text-lg text-slate-800 mb-2">No Homework Assigned</h3>
          <p className="text-slate-500 mb-4">{isTeacherOrAdmin ? 'Create your first homework assignment' : 'No homework has been assigned yet'}</p>
        </div>
      )}

      <RightSlideModal 
        isOpen={showModal} 
        onClose={() => setShowModal(false)} 
        title={`${selectedHw ? 'Edit' : 'Assign'} Homework`}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Title</label>
            <input type="text" value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} placeholder="e.g., Chapter 5 Exercises" className="w-full px-4 py-2 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 transition-all outline-none" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Description</label>
            <textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} placeholder="Homework instructions..." className="w-full px-4 py-2 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 transition-all outline-none" rows="3" required />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Academic Session</label>
              <select 
                value={modalSession} 
                onChange={(e) => {
                  setModalSession(e.target.value);
                  setFormData({...formData, classId: '', subjectId: ''});
                }} 
                className="w-full px-4 py-2 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 transition-all outline-none" 
                required
              >
                <option value="">Select Session</option>
                {sessions.map(s => <option key={s.id} value={s.sessionYear}>{s.sessionYear}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Class</label>
              <select 
                value={formData.classId} 
                onChange={(e) => setFormData({...formData, classId: e.target.value, subjectId: ''})} 
                className="w-full px-4 py-2 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 transition-all outline-none" 
                required 
                disabled={!modalSession}
              >
                <option value="">Select Class</option>
                {classes.filter(c => c.sessionYear === modalSession).map(c => (
                  <option key={c.id} value={c.id}>{c.className}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Subject</label>
            <select value={formData.subjectId} onChange={(e) => setFormData({...formData, subjectId: e.target.value})} className="w-full px-4 py-2 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 transition-all outline-none" required disabled={!formData.classId}>
              <option value="">Select Subject</option>
              {getSubjectsForClass(formData.classId).map(s => <option key={s.id} value={s.id}>{s.subjectName}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Due Date</label>
            <input type="date" value={formData.dueDate} onChange={(e) => setFormData({...formData, dueDate: e.target.value})} className="w-full px-4 py-2 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 transition-all outline-none" required />
          </div>
          <div className="flex gap-3 pt-4">
            <button type="submit" className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full py-2.5 font-medium">{selectedHw ? 'Update' : 'Assign'} Homework</button>
            <button type="button" onClick={() => setShowModal(false)} className="flex-1 border border-slate-200 hover:bg-slate-50 rounded-full py-2.5 font-medium">Cancel</button>
          </div>
        </form>
      </RightSlideModal>
    </div>
  );
}
